import Vue from "vue";
import VueRouter from "vue-router";
import RoutersCabine from "./cabine";
import store from "@/store/index";
import {
  manutencaoEfetiva,
  podeLiberarManutencao,
} from "@/service/totemOperation";
import {
  currentAttendance,
  eventCodesRequired,
} from "@/service/eventAttendance";
import {
  deveAdiarManutencao,
  deveAguardarResultadoPagamento,
  encerrarSessaoCompraParaManutencao,
} from "@/service/purchaseSession";
import { reportarErro } from "@/service/monitoramentoErros";
Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "root",
    redirect: "/404",
  },
  {
    path: "/error-config",
    name: "errorConfig",
    component: () => import("@/views/error-config.vue"),
    beforeEnter: (to, from, next) => {
      const totem = store.getters["totem/getTotem"];
      if (totem.id && totem.id.length > 3) {
        next();
        return;
      }

      // Num reload direto o Vuex volta vazio. Repassar pelo init recupera a
      // autenticação e reinstala tanto a manutenção quanto o clean_cache.
      const totemId = localStorage.getItem("totemId");
      next(totemId ? `/init/${totemId}` : "/404");
    },
  },
  {
    path: "/init/:id",
    name: "initializeTotem",
    component: () => import("@/views/init/index.vue"),
  },
  {
    path: "/manutencao",
    name: "manutencao",
    component: () => import("@/views/cabine/vManutencao.vue"),
    beforeEnter: (to, from, next) => {
      const totem = store.getters["totem/getTotem"];
      if (totem.id && totem.id.length > 3) {
        // Falha fechada: somente `false` publicado explicitamente libera a
        // rota. Campo ausente ou inválido não deve retirar um totem bloqueado.
        if (podeLiberarManutencao(totem)) next("/cabine/home");
        else next();
        return;
      }

      const totemId = localStorage.getItem("totemId");
      next(totemId ? `/init/${totemId}` : "/404");
    },
  },
  {
    path: "/cabine",
    name: "cabine",
    component: () =>
      import(/* webpackChunkName: "cabine" */ "@/views/cabine/index.vue"),
    children: RoutersCabine,
    redirect: "/cabine/home",
    beforeEnter: (to, from, next) => {
      //regra para bloquear sem o init
      const totem = store.getters["totem/getTotem"];
      if (totem.id && totem.id.length > 3) {
        next();
      } else {
        next("/404");
      }
    },
  },
  {
    path: "/404",
    name: "error-404",
    component: () => import("@/views/404.vue"),
  },
  {
    path: "*",
    redirect: "/404",
  },
];

if (process.env.VUE_APP_ENV !== "production") {
  routes.push({
    path: "/dev",
    name: "dev",
    component: () => import("@/views/cabine/editor/vEditor.vue"),
  });
}

const router = new VueRouter({
  mode: "history",
  routes,
});

// A manutenção aguarda o resultado de uma cobrança em andamento e o retorno
// à Home de uma compra aprovada. Depois encerra a escuta antes que callbacks
// tardios consigam iniciar outra jornada.
// `/init/:id` continua permitido para restaurar autenticação/identidade após
// um reload; nenhuma flag é alterada pelo cliente.
router.beforeEach((to, from, next) => {
  const totem = store.getters["totem/getTotem"];
  const manutencaoAtiva =
    manutencaoEfetiva(totem) ||
    (from.path === "/manutencao" && !podeLiberarManutencao(totem));
  const inicializando = to.name === "initializeTotem";

  if (manutencaoAtiva && !inicializando) {
    const retornaHome = to.path === "/cabine/home";
    const entraManutencao = to.path === "/manutencao";
    if (
      deveAguardarResultadoPagamento(store, from.path) &&
      (retornaHome || entraManutencao)
    ) {
      next(false);
      return;
    }
    if (deveAdiarManutencao(store, from.path) && !retornaHome) {
      if (entraManutencao) next(false);
      else next();
      return;
    }
    encerrarSessaoCompraParaManutencao(store);
    if (entraManutencao) next();
    else next("/manutencao");
    return;
  }
  const totemId = store.getters["totem/getTotem"]?.id;
  if (
    to.path.startsWith("/cabine/") &&
    !["homeCabine", "eventCode", "eventPhotoQuota"].includes(to.name)
  ) {
    try {
      const session = currentAttendance(totemId);
      const compraId = store.getters["compra/getCompra"]?.id;
      if (
        (eventCodesRequired(totemId) && !session) ||
        (session && session.purchaseId !== compraId)
      ) {
        next("/cabine/codigo-evento");
        return;
      }
    } catch (_) {
      next("/cabine/codigo-evento");
      return;
    }
  }
  next();
});

// ── Falha ao baixar o chunk de uma rota ───────────────────────────────────────
// Todas as telas são carregadas sob demanda (`() => import(...)`). Se o arquivo
// não baixar — rede oscilando, ou um deploy novo que apagou o chunk antigo que
// esta aba ainda referencia — o vue-router aborta a navegação e o <router-view>
// não renderiza NADA: tela totalmente branca, inclusive ao voltar para a Home.
// Recarregar a página busca o bundle atual e devolve o totem ao ar.
const RECARGA_CHUNK_KEY = "chunkReloadedAt";
const RECARGA_CHUNK_COOLDOWN_MS = 30000;

function ehFalhaDeChunk(erro) {
  const mensagem = (erro && (erro.message || erro.toString())) || "";
  return /Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError/i.test(
    mensagem
  );
}

function recarregouChunkRecentemente() {
  try {
    const t = Number(sessionStorage.getItem(RECARGA_CHUNK_KEY) || 0);
    return !!t && Date.now() - t < RECARGA_CHUNK_COOLDOWN_MS;
  } catch (_) {
    return false;
  }
}

router.onError((erro) => {
  if (!ehFalhaDeChunk(erro)) {
    // Erro lançado num guard ou componente assíncrono: navegação abortada.
    reportarErro(erro, { origem: "router" });
    return;
  }
  // Cooldown: se o chunk continuar indisponível (ex.: totem offline), não entra
  // em laço de reload — a tela de erro/última rota permanece e o totem tenta de
  // novo na próxima navegação.
  if (recarregouChunkRecentemente()) return;
  try {
    sessionStorage.setItem(RECARGA_CHUNK_KEY, String(Date.now()));
  } catch (_) {
    // ignora erros de storage — o reload ainda ocorre
  }
  window.location.reload();
});

export default router;
